import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Phone, Mail, MapPin, Download, Share2, ImageDown, Loader2, Globe, BookOpen, ChevronRight,
  Facebook, Instagram, Twitter, Youtube, Linkedin, Link2,
} from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { WhatsAppIcon, ThreadsIcon } from "@/components/public/BrandIcons";
import { getDigitalCard, SHARE_IMAGE_URL } from "@/lib/api";

function buildVcf(card) {
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${card.name}`,
    `TITLE:${card.role}`,
    card.phone && `TEL;TYPE=CELL,VOICE:${card.phone}`,
    card.whatsapp && card.whatsapp !== card.phone && `TEL;TYPE=CELL:${card.whatsapp}`,
    card.email && `EMAIL;TYPE=INTERNET:${card.email}`,
    card.address && `ADR;TYPE=WORK:;;${card.address.replace(/\n/g, ", ")};;;;`,
    `URL:${window.location.origin}`,
    `NOTE:${card.tagline || ""}`,
    "END:VCARD",
  ].filter(Boolean).join("\r\n");
}

async function fetchShareImageFile() {
  const res = await fetch(SHARE_IMAGE_URL);
  if (!res.ok) throw new Error("Could not load card image");
  const blob = await res.blob();
  return new File([blob], "girish-kumar-naidu-card.png", { type: blob.type || "image/png" });
}

export default function DigitalCard() {
  const { data: card } = useQuery({ queryKey: ["digital-card"], queryFn: getDigitalCard });
  // Pre-fetch the share image so navigator.share() runs inside the click gesture
  // (awaiting a slow fetch inside the handler voids the user-activation and share fails)
  const { data: shareFile } = useQuery({
    queryKey: ["share-image-file"],
    queryFn: fetchShareImageFile,
    staleTime: Infinity,
    retry: 1,
  });
  const { lang, tr } = useLanguage();
  const [sharing, setSharing] = useState(false);

  if (!card) return null;

  const cardUrl = `${window.location.origin}/digital-card`;
  const social = card.socialLinks || {};
  const name = (lang === "kn" && card.name_kn) || card.name;
  const role = (lang === "kn" && card.role_kn) || card.role;

  const saveContact = () => {
    const blob = new Blob([buildVcf(card)], { type: "text/vcard;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "girish-kumar-naidu.vcf";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Contact file downloaded");
  };

  const shareWhatsApp = () => {
    const text = `${card.name} — ${card.role}\n${card.tagline || ""}\n\nDigital card: ${cardUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      return true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = cardUrl;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    }
  };

  // Share the card WITH the generated image (mobile share sheet supports files)
  const shareCard = async () => {
    setSharing(true);
    try {
      const file = shareFile || (await fetchShareImageFile().catch(() => null));
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: card.name, text: `${card.name} — ${card.role}\n${cardUrl}` });
      } else if (navigator.share) {
        await navigator.share({ title: card.name, text: card.tagline, url: cardUrl });
      } else {
        throw new Error("share-unsupported");
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user closed the share sheet — not an error
      const copied = await copyLink();
      if (copied) {
        toast.success("Sharing isn't supported on this browser — card link copied. Paste it in WhatsApp or anywhere to share.");
      } else {
        toast.info(`Copy this link to share: ${cardUrl}`);
      }
    } finally {
      setSharing(false);
    }
  };

  const downloadImage = async () => {
    try {
      const file = await fetchShareImageFile();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Card image downloaded");
    } catch {
      toast.error("Could not download the card image");
    }
  };

  return (
    <div data-testid="digital-card-page">
      <PageSeo
        slug="digital-card"
        title="Digital Card | Girish Kumar Naidu — GBA Hoodi Ward 29"
        description="Shareable digital visiting card of Girish Kumar Naidu, aspirant candidate for GBA Hoodi Ward 29. Save the contact or share with neighbours."
        keywords="Girish Kumar Naidu digital card, contact card, Hoodi Ward 29"
        image={SHARE_IMAGE_URL}
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Digital Card" }])]}
      />
      <PageHeader
        overline={tr("page.card.overline")}
        title={tr("page.card.title")}
        subtitle={tr("page.card.subtitle")}
        crumbs={[{ name: "Digital Card" }]}
      />

      <section className="bg-navy py-10 pb-28 md:py-24 md:pb-24 relative grain overflow-hidden">
        <p className="absolute -bottom-10 left-0 right-0 text-center font-display font-black text-[16rem] leading-none text-stroke-light select-none pointer-events-none hidden lg:block" aria-hidden="true">
          29
        </p>
        <div className="max-w-5xl mx-auto px-4 sm:px-5 relative grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* The card */}
          <Reveal>
            <div className="glass-dark rounded-2xl sm:rounded-3xl overflow-hidden max-w-md mx-auto w-full shadow-2xl" data-testid="digital-card">
              {/* Saffron ribbon header */}
              <div className="bg-gradient-to-r from-gold to-gold-light px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-white">Digital Card</span>
                <span className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-white/20 flex items-center justify-center font-display font-black text-white">G</span>
              </div>

              {/* Photo + identity */}
              <div className="relative">
                {card.photo && <img src={card.photo} alt={card.name} className="w-full aspect-[4/3] object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-4 sm:p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-1">GBA Hoodi Ward 29</p>
                  <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight" data-testid="card-name">{name}</h2>
                  <p className="text-slate-300 text-xs sm:text-sm mt-1">{role}</p>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {card.tagline && (
                  <p className="text-slate-300 text-sm italic border-l-2 border-gold pl-3">{card.tagline}</p>
                )}

                <div className="space-y-2.5 text-sm">
                  {card.phone && (
                    <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-slate-200 hover:text-gold transition-colors" data-testid="card-phone">
                      <Phone className="h-4 w-4 text-gold shrink-0" /> {card.phone}
                    </a>
                  )}
                  {card.whatsapp && (
                    <a href={`https://wa.me/${(card.whatsapp || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-200 hover:text-gold transition-colors">
                      <WhatsAppIcon className="h-4 w-4 text-civic shrink-0" /> {card.whatsapp}
                    </a>
                  )}
                  {card.email && (
                    <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-slate-200 hover:text-gold transition-colors">
                      <Mail className="h-4 w-4 text-gold shrink-0" /> {card.email}
                    </a>
                  )}
                  {card.address && (
                    <p className="flex items-start gap-3 text-slate-300">
                      <MapPin className="h-4 w-4 text-gold shrink-0 mt-0.5" /> {card.address}
                    </p>
                  )}
                </div>

                {/* Quick links: website + biography */}
                <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
                  <a
                    href={card.website || window.location.origin}
                    target={card.website ? "_blank" : "_self"}
                    rel="noreferrer"
                    data-testid="card-website-link"
                    className="flex items-center justify-between gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-white/15 text-slate-200 text-[13px] sm:text-sm font-semibold hover:border-gold hover:text-gold transition-colors"
                  >
                    <span className="flex items-center gap-2 min-w-0"><Globe className="h-4 w-4 text-gold shrink-0" /> <span className="truncate">{tr("card.website")}</span></span>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  </a>
                  <Link
                    to="/biography"
                    data-testid="card-biography-link"
                    className="flex items-center justify-between gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-white/15 text-slate-200 text-[13px] sm:text-sm font-semibold hover:border-gold hover:text-gold transition-colors"
                  >
                    <span className="flex items-center gap-2 min-w-0"><BookOpen className="h-4 w-4 text-gold shrink-0" /> <span className="truncate">{tr("card.biography")}</span></span>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  </Link>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap">
                  {[
                    { name: "facebook", icon: Facebook, url: social.facebook },
                    { name: "instagram", icon: Instagram, url: social.instagram },
                    { name: "twitter", icon: Twitter, url: social.twitter },
                    { name: "youtube", icon: Youtube, url: social.youtube },
                    { name: "linkedin", icon: Linkedin, url: social.linkedin },
                    { name: "threads", icon: ThreadsIcon, url: social.threads },
                  ].filter((s) => s.url).map((s) => (
                    <a key={s.url} href={s.url} target="_blank" rel="noreferrer" title={s.name} data-testid={`card-social-${s.name}`} className="h-9 w-9 rounded-lg border border-white/15 flex items-center justify-center text-slate-300 hover:bg-gold hover:border-gold hover:text-white transition-colors">
                      <s.icon className="h-4 w-4" />
                    </a>
                  ))}
                  {(card.customLinks || []).filter((l) => l.url).map((l, i) => (
                    <a key={`${l.url}-${i}`} href={l.url} target="_blank" rel="noreferrer" title={l.label} aria-label={l.label} data-testid={`card-custom-link-${i}`} className="h-9 w-9 rounded-lg border border-white/15 flex items-center justify-center text-slate-300 hover:bg-gold hover:border-gold hover:text-white transition-colors">
                      {l.svg ? (
                        <span className="h-4 w-4 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:fill-current" dangerouslySetInnerHTML={{ __html: l.svg }} />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                    </a>
                  ))}
                </div>

                <div className="flex items-center gap-3 sm:gap-4 bg-navy/60 border border-white/10 rounded-2xl p-3 sm:p-4" data-testid="card-qr">
                  <div className="bg-white p-1.5 sm:p-2 rounded-xl shrink-0">
                    <QRCodeSVG value={cardUrl} size={80} fgColor="#0A192F" />
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Scan to open this card on any phone — or use the buttons to save and share.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:gap-2.5 pt-1">
                  <button onClick={saveContact} data-testid="card-save-contact"
                    className="col-span-2 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-gold text-white font-bold btn-saffron text-sm sm:text-base">
                    <Download className="h-4 w-4" /> {tr("card.saveContact")} (.vcf)
                  </button>
                  <button onClick={shareWhatsApp} data-testid="card-share-whatsapp"
                    className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-full bg-[#25D366] text-white font-bold text-[13px] sm:text-sm hover:bg-[#1ebe5b] transition-colors">
                    <WhatsAppIcon className="h-4 w-4" /> {tr("cta.whatsapp")}
                  </button>
                  <button onClick={shareCard} disabled={sharing} data-testid="card-share"
                    className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-full border border-white/20 text-white font-bold text-[13px] sm:text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
                    {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} {tr("card.share")}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Share image preview */}
          <Reveal delay={0.15}>
            <div className="max-w-md mx-auto w-full">
              <p className="inline-flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-gold mb-4">
                <span className="h-2 w-2 bg-gold inline-block" /> {tr("card.imageTitle")}
              </p>
              <div className="rounded-2xl sm:rounded-3xl overflow-hidden border border-white/15 shadow-2xl" data-testid="card-share-image-preview">
                <img src={SHARE_IMAGE_URL} alt="Shareable card" className="w-full" />
              </div>
              <p className="text-slate-400 text-xs sm:text-sm mt-4 leading-relaxed">{tr("card.imageDesc")}</p>
              <button onClick={downloadImage} data-testid="card-download-image"
                className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-navy font-bold text-sm hover:bg-paper transition-colors">
                <ImageDown className="h-4 w-4" /> {tr("card.downloadImage")}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
